import { gql } from '@apollo/client';
import {
  AlertDescription,
  Box,
  Button,
  chakra,
  Checkbox,
  HStack,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { SwitchType } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { HOUR_REGEX } from '~utils/timeBlocks';
import { useBulkUpdateAutoRestartMutation } from './__generated__/BulkUpdateAutoRestart.graphql';
import BackToAllActionsButton from './BackToAllActionsButton';
import { BulkActionComponent } from './BulkActionModal';

const schema = z.object({
  displayIds: z.array(z.string()),
  isEnabled: z.boolean(),
  time: z.string(),
});
type FormValues = z.TypeOf<typeof schema>;

export const BulkUpdateAutoRestart: BulkActionComponent = ({
  displays,
  onCancel,
  onBack,
  onSuccess,
}) => {
  const [bulkUpdateAutoRestart] = useBulkUpdateAutoRestartMutation();
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      displayIds: displays
        .filter(({ autoRestart }) => !_.isNil(autoRestart?.desired ?? autoRestart?.reported))
        .map((display) => display.id),
      isEnabled: true,
      time: '00:00',
    },
    resolver: zodResolver(schema),
  });
  const isEnabled = useWatch({
    control,
    name: 'isEnabled',
  });
  console.log(isEnabled);
  const toast = useToast();
  const analytics = useAnalyticsReporter();
  const autoRestartNotSupported = displays.filter((d) => !d.autoRestart);
  const autoRestartNotSupportedCount = autoRestartNotSupported.length;
  const autoRestartNotSupportedAlias =
    autoRestartNotSupportedCount === 1 ? autoRestartNotSupported[0].alias : null;

  const handleBulkUpdateAutoRestart = async ({ displayIds, isEnabled, time }: FormValues) => {
    try {
      const switchValue = isEnabled ? SwitchType.On : SwitchType.Off;

      if (displayIds.length > 0 && time.length === 5) {
        await bulkUpdateAutoRestart({
          variables: {
            input: {
              displayIds,
              switch: switchValue,
              time,
            },
          },
        });

        if (displayIds.length === 1) {
          analytics.track('autoRestartUpdate');
        } else {
          analytics.track('displayBulkActionComplete', {
            action: 'updateAutoRestart',
            displayCount: displayIds.length,
          });
        }
      }

      await onSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: t('bulkAutoRestartErr'),
        description: fromError(err, 'bulkUpdateAutoRestart'),
      });
    }
  };

  return (
    <>
      <ModalContent>
        <ModalHeader>{t('autorestart')}</ModalHeader>
        <ModalCloseButton onClick={onCancel} isDisabled={isSubmitting} />
        <form onSubmit={handleSubmit(handleBulkUpdateAutoRestart)}>
          {autoRestartNotSupportedCount > 0 && (
            <Box px="7" py="8">
              <WarningAlert>
                <AlertDescription>
                  {' '}
                  <Trans
                    i18nKey="autoRestartNotSupported"
                    count={autoRestartNotSupportedCount}
                    values={{
                      name: autoRestartNotSupportedAlias,
                      count: autoRestartNotSupportedCount,
                    }}
                    components={{
                      strong: <chakra.strong />,
                    }}
                  />
                </AlertDescription>
              </WarningAlert>
            </Box>
          )}
          {autoRestartNotSupportedCount == 0 && (
            <ModalBody>
              <Stack spacing="4">
                <InfoAlert>{t('autoRestartSkipAlert')}</InfoAlert>
                <Text color="gray.500" mb="2">
                  {t('autoRestartSetAlert')}
                </Text>
                <Controller
                  name="isEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox isChecked={field.value} onChange={field.onChange}>
                      {t('enableAutoRestart')}
                    </Checkbox>
                  )}
                />
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <TimeSelect
                      property="start"
                      value={isEnabled ? field.value : '--:--'}
                      onChange={(property, value) => {
                        if (!HOUR_REGEX.test(value)) return;
                        field.onChange(value);
                      }}
                      disabled={!isEnabled}
                    />
                  )}
                />
              </Stack>
            </ModalBody>
          )}
          <ModalFooter>
            <HStack flex="1" alignItems="center">
              <BackToAllActionsButton onBack={onBack} isDisabled={isSubmitting} />
              <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  onClick={onCancel}
                  isDisabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button
                  marginLeft="3"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  isDisabled={isSubmitting || (autoRestartNotSupportedCount ?? 0) > 0}
                  isLoading={isSubmitting}
                >
                  {t('apply')}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </>
  );
};

BulkUpdateAutoRestart.graphql = {
  fragments: {
    BulkUpdateAutoRestart_display: gql`
      fragment BulkUpdateAutoRestart_display on Display {
        autoRestart {
          desired {
            switch
            time
          }
          reported {
            switch
            time
          }
        }
      }
    `,
  },
  mutations: {
    BulkUpdateAutoRestart: gql`
      mutation BulkUpdateAutoRestart($input: DisplayBulkUpdateAutoRestartInput!) {
        displayBulkUpdateAutoRestart(input: $input) {
          displays {
            id
            autoRestart {
              desired {
                switch
                time
              }
              reported {
                switch
                time
              }
            }
          }
        }
      }
    `,
  },
};
